import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing } from "lucide-react";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { fmtDateTime } from "../../lib/format";
import {
  DELIVERY_STATE_NOTES,
  MAX_DAYS_BEFORE,
  MAX_OFFSETS,
  MIN_DAYS_BEFORE,
  REMINDER_STATE_LABELS,
  addOffset,
  isHistoricalState,
  offsetChipLabel,
  offsetLabel,
  reminderApiError,
  removeOffset,
  sameOffsets,
  validateOffsets,
} from "../../lib/reminderSettings";

/**
 * Phase 3C.3 — "Автоматични напомняния" on the quotation detail page.
 *
 * AUTHORIZATION IS NOT DECIDED HERE. The card simply calls
 * GET/PUT /api/quotations/{id}/reminders; those endpoints authorize through the
 * EXISTING quotation access contract — the same three scopes the quotation list
 * and the quotation page already use — with the same generic 404 for anything
 * else. No role appears in this file, no new role rule exists in it, and
 * `can_edit` comes from the server.
 *
 * WHAT THE UI MUST NOT PRETEND
 *   * a SENT reminder is a historical record. It is rendered as history and
 *     carries no control at all — the database refuses to re-arm it, and
 *     offering a button that implies otherwise would be a lie;
 *   * a skipped / missed offset is an informational state, in Bulgarian, never
 *     an error;
 *   * a terminal quotation shows its reminder history read-only. The editor is
 *     not rendered, and the server refuses the write regardless.
 */
export default function ReminderCard({ quotationId, quotationStatus, onError }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [newOffset, setNewOffset] = useState("");
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/quotations/${quotationId}/reminders`);
      setData(r.data);
      setDraft(r.data?.days_before || []);
    } catch (err) {
      // Non-fatal: the reminder card is one section of the page, and a failure
      // here must never blank the quotation.
      reportError("quotationDetail.loadReminders", err);
      setData(null);
      if (onError) onError(err);
    }
  }, [quotationId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  // The quotation's own status decides whether reminders may still change, so a
  // status change elsewhere on the page must be reflected here.
  useEffect(() => {
    if (quotationStatus) load();
  }, [quotationStatus, load]);

  const startEditing = () => {
    setDraft(data?.days_before || []);
    setNewOffset("");
    setError("");
    setNotice("");
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(data?.days_before || []);
    setNewOffset("");
    setError("");
    setEditing(false);
  };

  const add = () => {
    const result = addOffset(draft, newOffset);
    if (result.error) {
      setError(result.error);
      return;
    }
    setError("");
    setNewOffset("");
    setDraft(result.list);
  };

  const save = async () => {
    if (inFlight.current) return;
    const invalid = validateOffsets(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      const r = await api.put(`/quotations/${quotationId}/reminders`, {
        days_before: draft,
      });
      setData(r.data);
      setDraft(r.data?.days_before || []);
      setEditing(false);
      setNotice(t("reminders.saved"));
    } catch (err) {
      reportError("quotationDetail.saveReminders", err);
      setError(reminderApiError(err, t("reminders.save_failed")));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  if (!data) return null;

  const rules = data.rules || [];
  // WHAT THIS CARD SHOWS IS NOT WHAT THE API RETURNS, ON PURPOSE.
  // `data.rules` is the quotation's LIFETIME projection: a cancelled offset
  // from an older schedule is genuine history the database keeps, and asking
  // the backend to hide it would destroy an audit trail. But rendering it as
  // configuration told the user their schedule was [7, 5, 2, 1, 0] when it was
  // [5, 1, 0]. `data.days_before` is the authoritative CURRENT schedule, so the
  // list is built FROM IT, in its order, and `rules` is only a lookup. No state
  // is ever used to infer what is configured.
  const configured = Array.isArray(data.days_before) ? data.days_before : [];
  // One rule per offset for the row's entire lifetime is a database invariant
  // (quotation_reminders_one_rule_per_offset), so this map is exact.
  const ruleByOffset = new Map(rules.map((rule) => [Number(rule.days_before), rule]));
  // No second filter, and no invented row. The server builds `days_before` from
  // these same rows via `configured_offsets`, which keeps only the raw statuses
  // that are still configuration (pending / processing / failed) and drops
  // sent / cancelled / skipped. So a configured offset always HAS a rule here,
  // and a sent one never appears. `filter(Boolean)` is the fail-safe: if that
  // contract were ever broken the card shows one row less, rather than
  // inventing a reminder state the database never reported.
  const currentRules = configured
    .map((days) => ruleByOffset.get(Number(days)))
    .filter(Boolean);
  const sentRules = rules.filter((rule) => rule.state === "sent");
  const canEdit = !!data.can_edit;
  const next = data.next_reminder;
  // Why there is no next reminder, when the server says so. Never invented
  // here: `delivery_state` is derived server-side from the same facts the
  // worker's claim joins on.
  const deliveryNote = DELIVERY_STATE_NOTES[data.delivery_state] || "";
  const dirty = !sameOffsets(draft, data.days_before || []);
  const full = draft.length >= MAX_OFFSETS;

  return (
    <div className="qw-card" style={{ marginBottom: 16 }} data-testid="quotation-reminders-section">
      <div className="hstack" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <h2 style={{ fontWeight: 600, fontSize: 14, margin: 0 }} className="qw-break">
          {t("reminders.card_title")}
        </h2>
        {canEdit && !editing && (
          <button
            type="button"
            className="qw-btn sm"
            onClick={startEditing}
            data-testid="btn-edit-reminders"
          >
            {t("reminders.edit")}
          </button>
        )}
      </div>

      {deliveryNote && (
        <div
          className="qw-help"
          style={{ marginBottom: 8 }}
          data-testid={`quotation-reminders-delivery-${data.delivery_state}`}
        >
          {deliveryNote}
        </div>
      )}

      {next && !editing && (
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            padding: 12,
            borderRadius: 8,
            background: "#ecfdf5",
            marginBottom: 12,
          }}
          data-testid="quotation-reminder-next"
        >
          <BellRing size={18} strokeWidth={2} aria-hidden="true" style={{ color: "#047857" }} />
          <div>
            <div style={{ fontSize: 12, color: "#047857", fontWeight: 600 }}>
              {t("reminders.next_title")}
            </div>
            <div style={{ fontWeight: 600 }} data-testid="quotation-reminder-next-when">
              {fmtDateTime(next.scheduled_for)}
            </div>
            <div style={{ fontSize: 13, color: "#334155" }} data-testid="quotation-reminder-next-offset">
              {offsetLabel(next.days_before)}
            </div>
            {/* The offer says "valid until X"; delivery may be a little later
                (an overdue-but-still-sendable rule, or a retry). Shown only
                when the two genuinely differ. */}
            {next.next_attempt_at && next.next_attempt_at !== next.scheduled_for && (
              <div
                style={{ fontSize: 12, color: "#64748b" }}
                data-testid="quotation-reminder-next-attempt"
              >
                {t("reminders.expected_at", {
                  when: fmtDateTime(next.next_attempt_at),
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Only when the generic sentence ADDS something. For awaiting_send /
          lapsed / stopped the delivery note above already says why nothing is
          coming, and printing both read as a contradiction. */}
      {!next && !editing && data.delivery_state === "active" && configured.length > 0 && (
        <div className="qw-help" style={{ marginBottom: 12 }} data-testid="quotation-reminder-no-next">
          {t("reminders.no_next")}
        </div>
      )}

      {/* Emptiness is a property of the CURRENT configuration, never of the
          lifetime rules: a quotation whose whole schedule was removed still
          has cancelled history rows. */}
      {configured.length === 0 && !editing && (
        <div className="qw-empty" data-testid="quotation-reminders-empty">
          {t("reminders.none_configured")}
        </div>
      )}

      {currentRules.length > 0 && !editing && (
        <h3
          style={{ fontWeight: 600, fontSize: 13, margin: "0 0 6px" }}
          data-testid="quotation-reminders-current-title"
        >
          {t("reminders.current_schedule_title")}
        </h3>
      )}

      {currentRules.length > 0 && !editing && (
        <div className="qw-kv" data-testid="quotation-reminder-rules">
          {currentRules.map((rule) => (
            <div
              className="qw-kv-row"
              key={rule.days_before}
              data-testid={`quotation-reminder-rule-${rule.days_before}`}
            >
              <dt>{offsetLabel(rule.days_before)}</dt>
              <dd>
                <span data-testid={`quotation-reminder-state-${rule.days_before}`}>
                  {REMINDER_STATE_LABELS[rule.state] || REMINDER_STATE_LABELS.scheduled}
                </span>
                {/* A sent rule shows WHEN it went out; every other rule shows
                    when it is (or was) due. Neither ever offers an action. */}
                <span style={{ color: "#64748b", marginLeft: 8, fontSize: 13 }}>
                  {rule.state === "sent"
                    ? fmtDateTime(rule.sent_at)
                    : fmtDateTime(rule.scheduled_for)}
                </span>
                {/* A rule that is being retried is shown with WHEN the next
                    attempt is expected. The attempt counter and the provider
                    error code behind that instant never leave the server. */}
                {rule.state === "retrying" && rule.next_attempt_at && (
                  <span
                    style={{ color: "#64748b", marginLeft: 8, fontSize: 12 }}
                    data-testid={`quotation-reminder-retry-${rule.days_before}`}
                  >
                    {t("reminders.retry_at", {
                      when: fmtDateTime(rule.next_attempt_at),
                    })}
                  </span>
                )}
                {isHistoricalState(rule.state) && (
                  <span
                    style={{ color: "#94a3b8", marginLeft: 8, fontSize: 12 }}
                    data-testid={`quotation-reminder-history-${rule.days_before}`}
                  >
                    {t("reminders.history_note")}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </div>
      )}

      {/* Sent reminders are the only history with customer-facing value: the
          customer really did receive them. Cancelled / skipped / missed rows
          are configuration history — the database and the audit trail keep
          them, but they are not something to show a salesperson as "history". */}
      {sentRules.length > 0 && !editing && (
        <div style={{ marginTop: 12 }}>
          <h3
            style={{ fontWeight: 600, fontSize: 13, margin: "0 0 6px" }}
            data-testid="quotation-reminders-sent-title"
          >
            {t("reminders.sent_history_title")}
          </h3>
          <div className="qw-kv" data-testid="quotation-reminder-sent-rules">
            {sentRules.map((rule) => (
              <div
                className="qw-kv-row"
                key={rule.days_before}
                data-testid={`quotation-reminder-sent-${rule.days_before}`}
              >
                <dt>{offsetLabel(rule.days_before)}</dt>
                <dd>
                  <span>{REMINDER_STATE_LABELS.sent}</span>
                  <span style={{ color: "#64748b", marginLeft: 8, fontSize: 13 }}>
                    {fmtDateTime(rule.sent_at)}
                  </span>
                </dd>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div data-testid="quotation-reminders-editor">
          <div className="qw-help" style={{ marginBottom: 8 }}>
            {t("reminders.editor_help", {
              max: MAX_OFFSETS,
              min: MIN_DAYS_BEFORE,
              maxDays: MAX_DAYS_BEFORE,
            })}
          </div>
          {draft.length === 0 ? (
            <div className="qw-help" data-testid="quotation-reminders-draft-empty">
              {t("reminders.editor_empty")}
            </div>
          ) : (
            <div className="hstack" style={{ flexWrap: "wrap", gap: 8 }}>
              {draft.map((days) => (
                <span
                  key={days}
                  data-testid={`quotation-reminder-draft-${days}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "#f1f5f9",
                    fontSize: 13,
                  }}
                >
                  {offsetChipLabel(days)}
                  <button
                    type="button"
                    className="qw-btn ghost sm"
                    aria-label={t("reminders.remove_offset", { label: offsetChipLabel(days) })}
                    data-testid={`quotation-reminder-draft-remove-${days}`}
                    onClick={() => setDraft(removeOffset(draft, days))}
                    style={{ padding: "0 4px", lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="hstack" style={{ gap: 8, marginTop: 12 }}>
            <input
              className="qw-input"
              type="text"
              inputMode="numeric"
              value={newOffset}
              disabled={full}
              placeholder={t("reminders.add_placeholder")}
              aria-label={t("reminders.add_label")}
              data-testid="quotation-reminder-new"
              style={{ maxWidth: 160 }}
              onChange={(e) => {
                setNewOffset(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button
              type="button"
              className="qw-btn sm"
              disabled={full}
              onClick={add}
              data-testid="quotation-reminder-add"
            >
              {t("reminders.add_action")}
            </button>
          </div>

          <div className="hstack" style={{ gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="qw-btn ghost"
              onClick={cancelEditing}
              data-testid="quotation-reminder-cancel"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="qw-btn primary"
              disabled={saving || !dirty}
              onClick={save}
              data-testid="quotation-reminder-save"
            >
              {saving ? t("reminders.saving") : t("reminders.save")}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="qw-error" style={{ marginTop: 8 }} data-testid="quotation-reminder-error">
          {error}
        </div>
      )}
      {notice && !editing && (
        <div
          className="qw-help"
          style={{ color: "#059669", marginTop: 8 }}
          data-testid="quotation-reminder-saved"
        >
          {notice}
        </div>
      )}

      <div className="qw-help" style={{ marginTop: 12 }} data-testid="quotation-reminder-schedule-note">
        {t("reminders.send_time_note", {
          time: data.send_time_local,
          timezone: data.timezone,
        })}
      </div>
    </div>
  );
}
